import { AdminPageHeader, AdminPageShell } from '@/components/patterns/admin-page'
import { SeasonalDesignEditor } from '@/components/seasonal/SeasonalDesignEditor'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { designQueries } from '@/lib/services/designs/designs.query'
import {
  merchandisingTagQueries,
  useMerchandisingTagGroups,
} from '@/lib/services/merchandising-tags/merchandising-tags.query'
import { flattenSeasonalContentTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { requireGlobalSeasonalContentAccess } from '@/lib/services/seasonal-content/seasonal-content.route'
import { storeQueries } from '@/lib/services/stores/stores.query'
import { createFileRoute } from '@tanstack/react-router'
import { ImagePlus } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/seasonal-content/create')({
  component: CreateSeasonalDesignPage,
  head: adminRouteTitleHead,
  loader: async ({ context, params }) => {
    const access = requireGlobalSeasonalContentAccess({ context, params, requireEdit: true })

    await Promise.all([
      context.queryClient.ensureQueryData(
        merchandisingTagQueries.index({ organizationId: params.orgId })
      ),
      context.queryClient.ensureQueryData(designQueries.default({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)),
      context.queryClient.ensureQueryData(storeQueries.themes({ organizationId: params.orgId })),
    ])

    return {
      ...access,
      canCreateStockImages: context.ability.can('create', 'stock-images'),
    }
  },
})

export function CreateSeasonalDesignPage() {
  const { orgId } = Route.useParams()
  const { canCreateStockImages, isReadOnly } = Route.useLoaderData()
  const { data: merchandisingTagGroups = [] } = useMerchandisingTagGroups({ organizationId: orgId })
  const seasonalTags = flattenSeasonalContentTagGroups(merchandisingTagGroups)

  return (
    <AdminPageShell className="pb-0 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden">
      <AdminPageHeader
        icon={ImagePlus}
        title="Create seasonal design"
        description="Compose the banner, wording, and layout as one reusable storefront look. You can save a draft before publishing it."
      />
      <SeasonalDesignEditor
        seasonalTags={seasonalTags}
        canCreateStockImages={canCreateStockImages}
        isReadOnly={isReadOnly}
        organizationId={orgId}
      />
    </AdminPageShell>
  )
}
